import { api } from "api"
import useIsCpoDeal, { getStatiqAccountId } from "components/ProjectForm/cpoDefaults"
import {
    IAccountRoleData,
    setAccountNSitesForm,
    setAccountNSitesFormValues,
} from "components/ProjectForm/ProjectFormSlice"
import { EAccountRole } from "enums/commonEnums"
import { ESubModuleIds } from "enums/ModuleEnum"
import { useFormik } from "formik"
import useIsSuperAdmin from "hooks/useIsSuperAdmin"
import { AccountBillingAddressDetails, AccountContactDetails, AccountsTableData } from "pages/Accounts/AccountsServices"
import usePermissions from "permissions/usePermissions"
import {
    useGetAccountRolesQuery,
    useGetAllSitesListQuery,
    useGetCitiesQuery,
    useGetCountryStatesQuery,
    useLazyGetAccountBySiteIdQuery,
    useLazyGetAccountsCompactedQuery,
} from "pages/Common/commonService"
import { useAddSiteTicketMutation } from "pages/Sites/SiteService"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { TStore } from "store/Store"
import * as Yup from "yup"
import { SiteOption } from "./useSites"

export enum EAccountFormFields {
    SELECTED_ROLES = "selectedRoles",
    ACCOUNT_ROLES = "accountRoles",
    DESTINATION_SITE = "destinationSite",
    SOURCE_SITE = "sourceSite",
}

export enum EAccountRoleFields {
    ACCOUNT = "account",
    BILLING_ADDRESS = "billing_address",
    CONTACT = "contact",
    CONTACT_TYPE_ID = "contact_type_id",
}

type Option = {
    id: string | number
    title: string
    disabled?: boolean
}

type AccountRoleData = {
    [EAccountRoleFields.ACCOUNT]: Option | null
    [EAccountRoleFields.BILLING_ADDRESS]: Option | null
    [EAccountRoleFields.CONTACT]: Option | null
    [EAccountRoleFields.CONTACT_TYPE_ID]: number | null
}

type RoleOptions = {
    billingAddresses: Option[]
    contacts: (Option & { contact_type_id?: number })[]
}

export type AccountFormValues = {
    [EAccountFormFields.SELECTED_ROLES]: Option[]
    [EAccountFormFields.ACCOUNT_ROLES]: Record<EAccountRole, AccountRoleData>
    [EAccountFormFields.DESTINATION_SITE]: SiteOption | null
    [EAccountFormFields.SOURCE_SITE]: SiteOption | null
}

const DEFAULT_THREE_ROLES = [EAccountRole.BUYER, EAccountRole.HOST, EAccountRole.CPO]

const accountRoleSchema = Yup.object().shape({
    [EAccountRoleFields.ACCOUNT]: Yup.object().nullable().required("Account is required"),
    [EAccountRoleFields.BILLING_ADDRESS]: Yup.object().nullable().required("Billing Address is required"),
    [EAccountRoleFields.CONTACT]: Yup.object().nullable().required("Contact is required"),
    [EAccountRoleFields.CONTACT_TYPE_ID]: Yup.number().nullable().required("Contact Type is required"),
})

const mapAccountDetailsToOptions = (accountData: any): RoleOptions => ({
    billingAddresses:
        accountData.billing_addresses?.map((addr: AccountBillingAddressDetails) => ({
            id: addr.id,
            title: `${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`,
        })) || [],
    contacts:
        accountData.account_contacts?.map((contact: AccountContactDetails) => ({
            id: contact.id,
            title: `${contact.name} (${contact.email})`,
            contact_type_id: contact.contact_type_id,
        })) || [],
})

const useAccountNSites = (
    hasThreeDefaultRoles: boolean = false,
    showSourceSite: boolean = false,
    subModuleId?: number
) => {
    const dispatch = useDispatch()
    const { account_n_sites_form_values, account_n_sites_form } = useSelector((state: TStore) => state.ProjectForm)
    const { user_id } = useSelector((state: TStore) => state.verifyLogin)
    const [showDestinationSites, setShowDestinationSites] = useState(false)
    const [showSourceSites, setShowSourceSites] = useState(false)

    const permissions = usePermissions()
    const hasAddAccountPermission = permissions.PROJECTS__ADD_ACCOUNT
    const addSitesPermission = permissions.SITES__ADD_TICKET
    const isSuperAdmin = useIsSuperAdmin()
    // Editing an existing account (billing address / contact) is super admin only for now.
    // Swap in the commented line to bring the permission back into play alongside it:
    // const hasEditAccountPermission = hasAddAccountPermission && isSuperAdmin
    const hasEditAccountPermission = isSuperAdmin

    const [isFranchisePartner, setIsFranchisePartner] = useState<boolean>(
        !!account_n_sites_form_values?.is_franchise_partner || !!account_n_sites_form?.is_franchise_partner
    )
    const [isFranchisePartnerLocked, setIsFranchisePartnerLocked] = useState<boolean>(false)

    const isCpoDeal = useIsCpoDeal()
    // Flipped once the accounts saved on the project (or held in the draft) have landed in formik, so
    // the CPO defaults below can tell "nothing selected yet" apart from "not restored yet".
    const [savedAccountsRestored, setSavedAccountsRestored] = useState(false)

    const [prefilledRoles, setPrefilledRoles] = useState<Set<EAccountRole>>(new Set())
    const [showAccountFormModal, setShowAccountFormModal] = useState(false)
    const [accountFormRole, setAccountFormRole] = useState<EAccountRole | null>(null)
    const [editAccountData, setEditAccountData] = useState<AccountsTableData | undefined>(undefined)
    const [isEditMode, setIsEditMode] = useState(false)

    const { data: accountRolesList } = useGetAccountRolesQuery()
    const { data: allSitesList } = useGetAllSitesListQuery()
    const [getAccountsCompacted] = useLazyGetAccountsCompactedQuery()
    const [getAccountBySiteId] = useLazyGetAccountBySiteIdQuery()
    const [addSiteTicket, { isLoading: isAddingSite }] = useAddSiteTicketMutation()

    const fetchAccountDetails = useCallback(
        async (accountId: number) => {
            const request = dispatch(
                (api as any).endpoints.getAccountDetails.initiate(accountId, { forceRefetch: true })
            ) as any

            try {
                return await request.unwrap()
            } finally {
                request.unsubscribe()
            }
        },
        [dispatch]
    )

    const defaultRoles = useMemo(() => {
        return hasThreeDefaultRoles ? DEFAULT_THREE_ROLES : [EAccountRole.BUYER]
    }, [hasThreeDefaultRoles])

    const isDefaultRole = useCallback(
        (roleType: EAccountRole) => {
            return defaultRoles.includes(roleType)
        },
        [defaultRoles]
    )

    const mapSiteToOption = useCallback(
        (site?: { id: number; name: string; site_id: string } | null): SiteOption | null => {
            if (!site) return null
            return {
                id: site.id,
                name: site.name,
                site_id: site.site_id,
                pincode: 0,
                address: "",
                state: "",
                state_id: 0,
                city: "",
                city_id: 0,
                country_id: 101,
                latitude: 0,
                longitude: 0,
            }
        },
        []
    )

    // Resolves the prefilled site for edit mode. Prefers the full site object captured
    // from the project-details response; falls back to the (paginated) sites list only
    // when the full object isn't available.
    const resolvePrefilledSite = useCallback(
        (storedSite?: SiteOption | null, siteId?: number | null): SiteOption | null => {
            if (storedSite?.id) return storedSite
            if (!siteId) return null
            return mapSiteToOption(allSitesList?.find(site => site.id === siteId))
        },
        [allSitesList, mapSiteToOption]
    )

    const getInitialValues = useCallback((): AccountFormValues => {
        const accountRoles: Record<EAccountRole, AccountRoleData> = {} as Record<EAccountRole, AccountRoleData>
        Object.values(EAccountRole).forEach(role => {
            accountRoles[role] = {
                [EAccountRoleFields.ACCOUNT]: null,
                [EAccountRoleFields.BILLING_ADDRESS]: null,
                [EAccountRoleFields.CONTACT]: null,
                [EAccountRoleFields.CONTACT_TYPE_ID]: null,
            }
        })

        const initialSelectedRoles: Option[] = defaultRoles.map(role => ({
            id: accountRolesList?.find(i => i.type === role)?.id || role,
            title: role,
            disabled: true,
        }))

        return {
            [EAccountFormFields.SELECTED_ROLES]: initialSelectedRoles,
            [EAccountFormFields.ACCOUNT_ROLES]: accountRoles,
            [EAccountFormFields.DESTINATION_SITE]: resolvePrefilledSite(
                account_n_sites_form.destination_site,
                account_n_sites_form.destination_site_id
            ),
            [EAccountFormFields.SOURCE_SITE]: resolvePrefilledSite(
                account_n_sites_form.source_site,
                account_n_sites_form.source_site_id
            ),
        }
    }, [defaultRoles, accountRolesList, account_n_sites_form, resolvePrefilledSite])

    const getValidationSchema = useCallback((selectedRoles: Option[]) => {
        const roleValidations: Record<string, any> = {}
        selectedRoles.forEach(role => {
            roleValidations[role.id as EAccountRole] = accountRoleSchema
        })

        return Yup.object().shape({
            [EAccountFormFields.SELECTED_ROLES]: Yup.array().min(1, "At least one role must be selected"),
            [EAccountFormFields.ACCOUNT_ROLES]: Yup.object().shape(roleValidations),
        })
    }, [])

    const formik = useFormik<AccountFormValues>({
        initialValues: account_n_sites_form_values
            ? {
                  [EAccountFormFields.SELECTED_ROLES]: account_n_sites_form_values.selectedRoles as Option[],
                  [EAccountFormFields.ACCOUNT_ROLES]: account_n_sites_form_values.accountRoles as Record<
                      EAccountRole,
                      AccountRoleData
                  >,
                  [EAccountFormFields.DESTINATION_SITE]:
                      (account_n_sites_form_values.destinationSite as SiteOption | null) ||
                      resolvePrefilledSite(
                          account_n_sites_form.destination_site,
                          account_n_sites_form.destination_site_id
                      ),
                  [EAccountFormFields.SOURCE_SITE]:
                      (account_n_sites_form_values.sourceSite as SiteOption | null) ||
                      resolvePrefilledSite(account_n_sites_form.source_site, account_n_sites_form.source_site_id),
              }
            : getInitialValues(),
        validationSchema: getValidationSchema([{ id: EAccountRole.BUYER, title: EAccountRole.BUYER }]),
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: values => {},
        enableReinitialize: true,
    })

    // The CPO defaults re-check the form after an await, where the formik closure would be stale.
    const formikValuesRef = useRef(formik.values)
    formikValuesRef.current = formik.values

    const getSelectedAccount = useCallback((role: EAccountRole) => {
        return formikValuesRef.current[EAccountFormFields.ACCOUNT_ROLES][role]?.[EAccountRoleFields.ACCOUNT] || null
    }, [])

    const isRoleSelected = useCallback((role: EAccountRole) => {
        return formikValuesRef.current[EAccountFormFields.SELECTED_ROLES].some(selected => selected.title === role)
    }, [])

    const [showNewSiteForm, setShowNewSiteForm] = useState(false)
    const [newSiteDetails, setNewSiteDetails] = useState({
        country: { id: 101, title: "India" } as { id: number; title: string } | null,
        siteName: "",
        siteAddress: "",
        pincode: "",
        state: null as { id: number; title: string } | null,
        city: null as { id: number; title: string } | null,
        latitude: "",
        longitude: "",
        isWarehouse: 0 as 0 | 1,
    })
    const [showNewSiteErrors, setShowNewSiteErrors] = useState(false)

    const { data: statesList } = useGetCountryStatesQuery(
        { country_id: newSiteDetails.country?.id || 101 },
        { skip: !newSiteDetails.country?.id }
    )
    const { data: citiesList } = useGetCitiesQuery(
        { state_id: newSiteDetails.state ? [newSiteDetails.state.id] : [] },
        { skip: !newSiteDetails.state?.id }
    )

    const statesOptions = useMemo(() => {
        return statesList?.map((state: any) => ({ id: state.id, title: state.name })) || []
    }, [statesList])

    const citiesOptions = useMemo(() => {
        return citiesList?.map((city: any) => ({ id: city.id, title: city.name })) || []
    }, [citiesList])

    const toggleNewSiteForm = () => {
        setShowNewSiteForm(prev => !prev)
        setShowNewSiteErrors(false)
        if (showNewSiteForm) {
            setNewSiteDetails({
                country: { id: 101, title: "India" },
                siteName: "",
                siteAddress: "",
                pincode: "",
                state: null,
                city: null,
                latitude: "",
                longitude: "",
                isWarehouse: 0,
            })
        }
    }

    const isValidLatitude = (lat: string) => {
        const num = parseFloat(lat)
        return !isNaN(num) && num >= -90 && num <= 90
    }

    const isValidLongitude = (lng: string) => {
        const num = parseFloat(lng)
        return !isNaN(num) && num >= -180 && num <= 180
    }

    const handleCoordinatePaste = (e: React.ClipboardEvent, field: "latitude" | "longitude") => {
        const pastedText = e.clipboardData.getData("text")

        const coordinateMatch = pastedText.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/)

        if (coordinateMatch) {
            e.preventDefault()

            const lat = parseFloat(coordinateMatch[1])
            const long = parseFloat(coordinateMatch[2])

            const roundedLat = Math.round(lat * 1000000) / 1000000
            const roundedLong = Math.round(long * 1000000) / 1000000

            setNewSiteDetails(prev => ({
                ...prev,
                latitude: roundedLat.toString(),
                longitude: roundedLong.toString(),
            }))
        }
    }

    const handleNewSiteFieldChange = (field: keyof typeof newSiteDetails, value: any) => {
        setNewSiteDetails(prev => {
            const updated = { ...prev }

            if ((field === "latitude" || field === "longitude") && typeof value === "string") {
                const num = parseFloat(value)
                if (!isNaN(num)) {
                    const rounded = Math.round(num * 1000000) / 1000000
                    updated[field] = rounded.toString()
                    return updated
                }
            }

            ;(updated as Record<string, any>)[field] = value

            if (field === "country") {
                updated.state = null
                updated.city = null
            }
            if (field === "state") {
                updated.city = null
            }
            return updated
        })
    }

    const handleAddNewSite = async () => {
        setShowNewSiteErrors(true)
        const { siteName, siteAddress, pincode, state, city, latitude, longitude, country } = newSiteDetails

        if (!siteName || !siteAddress || !pincode || !state?.id || !city?.id || !latitude || !longitude) {
            return false
        }

        if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) {
            return false
        }

        try {
            await addSiteTicket({
                name: siteName,
                city_id: city.id,
                state_id: state.id,
                latitude: latitude,
                longitude: longitude,
                address: siteAddress,
                pincode: pincode,
                country_id: country?.id || 101,
                is_warehouse: newSiteDetails.isWarehouse,
                user_id: user_id,
            }).unwrap()

            setShowNewSiteForm(false)
            setNewSiteDetails({
                country: { id: 101, title: "India" },
                siteName: "",
                siteAddress: "",
                pincode: "",
                state: null,
                city: null,
                latitude: "",
                longitude: "",
                isWarehouse: 0,
            })
            setShowNewSiteErrors(false)
            return true
        } catch (error) {
            console.error("Error adding new site:", error)
            return false
        }
    }

    const [roleOptionsMap, setRoleOptionsMap] = useState<Record<EAccountRole, RoleOptions>>(() => {
        const initial = {} as Record<EAccountRole, RoleOptions>
        accountRolesList?.forEach(role => {
            initial[role.type] = { billingAddresses: [], contacts: [] }
        })
        return initial
    })

    const [loadingMap, setLoadingMap] = useState<Record<EAccountRole, boolean>>(() => {
        const initial: Record<EAccountRole, boolean> = {} as Record<EAccountRole, boolean>
        Object.values(EAccountRole).forEach(role => {
            initial[role] = false
        })
        return initial
    })

    const accountRolesOptions: Option[] = useMemo(() => {
        return (
            accountRolesList?.map(role => ({
                id: role.id,
                title: role.type,
                disabled: isDefaultRole(role.type),
            })) || []
        )
    }, [accountRolesList, isDefaultRole])

    const isInitializedRef = useRef(false)
    const hasPrefilledFromSiteRef = useRef(false)

    useEffect(() => {
        if (isInitializedRef.current) return

        const initialize = async () => {
            if (account_n_sites_form_values) {
                isInitializedRef.current = true
                // formik was initialized straight from these values, so they are already selected —
                // only the billing/contact option lists are refetched below.
                setSavedAccountsRestored(true)
                const selectedRoles = account_n_sites_form_values.selectedRoles as Option[]
                const accountRoles = account_n_sites_form_values.accountRoles as Record<EAccountRole, AccountRoleData>

                for (const role of selectedRoles) {
                    const roleType = role.title as EAccountRole
                    const selectedAccount = accountRoles[roleType]?.[EAccountRoleFields.ACCOUNT]
                    if (!selectedAccount?.id) continue

                    setLoadingMap(prev => ({ ...prev, [roleType]: true }))
                    try {
                        const accountData = await fetchAccountDetails(selectedAccount.id as number)
                        if (accountData) {
                            setRoleOptionsMap(prev => ({
                                ...prev,
                                [roleType]: mapAccountDetailsToOptions(accountData),
                            }))
                        }
                    } catch (error) {
                        console.error(`Error refetching account details for ${roleType}:`, error)
                    } finally {
                        setLoadingMap(prev => ({ ...prev, [roleType]: false }))
                    }
                }
                return
            }

            if (
                !account_n_sites_form?.account_roles?.length ||
                account_n_sites_form_values ||
                !accountRolesList?.length
            ) {
                return
            }

            isInitializedRef.current = true

            const accountRolesData: Record<EAccountRole, AccountRoleData> = {} as Record<EAccountRole, AccountRoleData>
            const selectedRoles: Option[] = []

            Object.values(EAccountRole).forEach(role => {
                accountRolesData[role] = {
                    [EAccountRoleFields.ACCOUNT]: null,
                    [EAccountRoleFields.BILLING_ADDRESS]: null,
                    [EAccountRoleFields.CONTACT]: null,
                    [EAccountRoleFields.CONTACT_TYPE_ID]: null,
                }
            })

            for (const roleData of account_n_sites_form.account_roles) {
                const roleInfo = accountRolesList.find(r => r.id === roleData.account_type_id)
                if (!roleInfo) continue

                const roleType = roleInfo.type as EAccountRole
                const roleId = roleInfo.id

                selectedRoles.push({
                    id: roleId,
                    title: roleType,
                    disabled: defaultRoles.includes(roleType),
                })

                try {
                    const accountDetails = await fetchAccountDetails(roleData.account_id)
                    const options = mapAccountDetailsToOptions(accountDetails)

                    const billingAddress = accountDetails.billing_addresses?.find(
                        (ba: AccountBillingAddressDetails) => ba.id === roleData.billing_address_id
                    )
                    const contact = roleData.contact_id
                        ? accountDetails.account_contacts?.find(
                              (c: AccountContactDetails) => c.id === roleData.contact_id
                          )
                        : null

                    accountRolesData[roleType] = {
                        [EAccountRoleFields.ACCOUNT]: {
                            id: roleData.account_id,
                            title: accountDetails.account_name,
                        },
                        [EAccountRoleFields.BILLING_ADDRESS]: billingAddress
                            ? {
                                  id: billingAddress.id,
                                  title: `${billingAddress.address}, ${billingAddress.city}, ${billingAddress.state} - ${billingAddress.pincode}`,
                              }
                            : null,
                        [EAccountRoleFields.CONTACT]: contact
                            ? {
                                  id: contact.id,
                                  title: `${contact.name} (${contact.email})`,
                              }
                            : null,
                        [EAccountRoleFields.CONTACT_TYPE_ID]: roleData.contact_type_id || null,
                    }

                    setRoleOptionsMap(prev => ({ ...prev, [roleType]: options }))
                } catch (error) {
                    console.error(`Error fetching account details for ${roleType}:`, error)
                }
            }

            defaultRoles.forEach(defaultRole => {
                if (!selectedRoles.some(r => r.title === defaultRole)) {
                    selectedRoles.unshift({
                        id: defaultRole,
                        title: defaultRole,
                        disabled: true,
                    })
                }
            })

            formik.setValues({
                ...formik.values,
                [EAccountFormFields.SELECTED_ROLES]: selectedRoles,
                [EAccountFormFields.ACCOUNT_ROLES]: accountRolesData,
                [EAccountFormFields.DESTINATION_SITE]: resolvePrefilledSite(
                    account_n_sites_form.destination_site,
                    account_n_sites_form.destination_site_id
                ),
                [EAccountFormFields.SOURCE_SITE]: resolvePrefilledSite(
                    account_n_sites_form.source_site,
                    account_n_sites_form.source_site_id
                ),
            })

            setSavedAccountsRestored(true)
        }

        initialize()
    }, [account_n_sites_form, account_n_sites_form_values, accountRolesList, allSitesList])

    const handleRoleChange = useCallback(
        (newRoles: Option[] | null) => {
            if (!newRoles) return

            const currentRoles = formik.values[EAccountFormFields.SELECTED_ROLES]

            let updatedRoles = [...newRoles]
            defaultRoles.forEach(defaultRole => {
                const hasRole = updatedRoles.some(role => role.title === defaultRole)
                if (!hasRole) {
                    updatedRoles = [{ id: defaultRole, title: defaultRole, disabled: true }, ...updatedRoles]
                }
            })

            updatedRoles = updatedRoles.map(role => ({
                ...role,
                disabled: isDefaultRole(role.title as EAccountRole),
            }))

            const removedRoles = currentRoles.filter(
                currentRole => !updatedRoles.some(updatedRole => updatedRole.title === currentRole.title)
            )

            removedRoles.forEach(removedRole => {
                const roleType = removedRole.title as EAccountRole

                formik.setFieldValue(`${EAccountFormFields.ACCOUNT_ROLES}.${roleType}`, {
                    [EAccountRoleFields.ACCOUNT]: null,
                    [EAccountRoleFields.BILLING_ADDRESS]: null,
                    [EAccountRoleFields.CONTACT]: null,
                })

                setRoleOptionsMap(prev => ({
                    ...prev,
                    [roleType]: { billingAddresses: [], contacts: [] },
                }))
            })

            formik.setFieldValue(EAccountFormFields.SELECTED_ROLES, updatedRoles)

            const newSchema = getValidationSchema(updatedRoles)
        },
        [formik, getValidationSchema, defaultRoles, isDefaultRole]
    )

    const handleAccountChange = useCallback(
        async (role: EAccountRole, selectedAccount: Option | null) => {
            formik.setFieldValue(
                `${EAccountFormFields.ACCOUNT_ROLES}.${role}.${EAccountRoleFields.ACCOUNT}`,
                selectedAccount
            )

            formik.setFieldValue(
                `${EAccountFormFields.ACCOUNT_ROLES}.${role}.${EAccountRoleFields.BILLING_ADDRESS}`,
                null
            )
            formik.setFieldValue(`${EAccountFormFields.ACCOUNT_ROLES}.${role}.${EAccountRoleFields.CONTACT}`, null)

            if (!selectedAccount) {
                setRoleOptionsMap(prev => ({
                    ...prev,
                    [role]: { billingAddresses: [], contacts: [] },
                }))
                return
            }

            setLoadingMap(prev => ({ ...prev, [role]: true }))

            try {
                const accountData = await fetchAccountDetails(selectedAccount.id as number)
                if (accountData) {
                    setRoleOptionsMap(prev => ({
                        ...prev,
                        [role]: mapAccountDetailsToOptions(accountData),
                    }))
                }
            } catch (error) {
                console.error("Error fetching account details:", error)
                setRoleOptionsMap(prev => ({
                    ...prev,
                    [role]: { billingAddresses: [], contacts: [] },
                }))
            } finally {
                setLoadingMap(prev => ({ ...prev, [role]: false }))
            }
        },
        [fetchAccountDetails, formik]
    )

    // Resolved once per form and reused, so toggling the franchise checkbox does not re-hit the API.
    const statiqAccountRef = useRef<Option | null>(null)

    const findStatiqAccount = useCallback(async (): Promise<Option | null> => {
        if (statiqAccountRef.current) return statiqAccountRef.current

        const statiqAccountId = getStatiqAccountId()
        if (!statiqAccountId) return null

        try {
            // The dropdown shows the name, so the configured id is resolved against the account.
            const accountData = await fetchAccountDetails(statiqAccountId)
            statiqAccountRef.current = accountData?.account_name
                ? { id: statiqAccountId, title: accountData.account_name }
                : null
        } catch (error) {
            console.error("Error resolving the Statiq account for the CPO defaults:", error)
            statiqAccountRef.current = null
        }

        return statiqAccountRef.current
    }, [fetchAccountDetails])

    const applyStatiqAccountDefault = useCallback(
        async (roles: EAccountRole[]) => {
            const pendingRoles = roles.filter(
                role => isRoleSelected(role) && !getSelectedAccount(role) && !prefilledRoles.has(role)
            )
            if (!pendingRoles.length) return

            const statiqAccount = await findStatiqAccount()
            if (!statiqAccount) return

            pendingRoles.forEach(role => {
                // Re-checked after the lookup: a site prefill may have filled the role meanwhile.
                if (getSelectedAccount(role)) return
                handleAccountChange(role, statiqAccount)
            })
        },
        [findStatiqAccount, getSelectedAccount, handleAccountChange, isRoleSelected, prefilledRoles]
    )

    // SB-8432: Buyer and CPO both default to Statiq's own account on a CPO deal. This runs on add and
    // edit alike, and only ever fills a role that is still empty — saved accounts, site-based prefills
    // and anything the user picked are all left alone.
    const hasAppliedCpoAccountDefaults = useRef(false)

    useEffect(() => {
        if (!isCpoDeal || hasAppliedCpoAccountDefaults.current) return
        if (!accountRolesList?.length) return

        const hasSavedAccounts = !!account_n_sites_form_values || !!account_n_sites_form?.account_roles?.length
        if (hasSavedAccounts && !savedAccountsRestored) return

        hasAppliedCpoAccountDefaults.current = true

        // A franchise buyer is the user's to pick, so the buyer default is skipped there.
        applyStatiqAccountDefault(
            isFranchisePartner || isFranchisePartnerLocked ? [EAccountRole.CPO] : [EAccountRole.CPO, EAccountRole.BUYER]
        )
    }, [
        isCpoDeal,
        accountRolesList,
        account_n_sites_form,
        account_n_sites_form_values,
        savedAccountsRestored,
        isFranchisePartner,
        isFranchisePartnerLocked,
        applyStatiqAccountDefault,
    ])

    const handleBillingAddressChange = useCallback(
        (role: EAccountRole, selectedAddress: Option | null) => {
            formik.setFieldValue(
                `${EAccountFormFields.ACCOUNT_ROLES}.${role}.${EAccountRoleFields.BILLING_ADDRESS}`,
                selectedAddress
            )
        },
        [formik]
    )

    const handleContactChange = useCallback(
        (role: EAccountRole, selectedContact: Option | null) => {
            formik.setFieldValue(
                `${EAccountFormFields.ACCOUNT_ROLES}.${role}.${EAccountRoleFields.CONTACT}`,
                selectedContact
            )
            const contact_type_id = roleOptionsMap[role].contacts.find(
                contact => contact.id === selectedContact?.id
            )?.contact_type_id
            formik.setFieldValue(
                `${EAccountFormFields.ACCOUNT_ROLES}.${role}.${EAccountRoleFields.CONTACT_TYPE_ID}`,
                contact_type_id
            )
        },
        [formik]
    )

    const getFieldError = useCallback(
        (role: EAccountRole, field: EAccountRoleFields): string | undefined => {
            if (formik.submitCount === 0) return undefined
            const roleData = formik.values[EAccountFormFields.ACCOUNT_ROLES][role]
            const hasValue = roleData?.[field]
            return !hasValue ? `${field} is required` : undefined
        },
        [formik.submitCount, formik.values]
    )

    const handleDeleteRole = useCallback(
        (roleToDelete: EAccountRole) => {
            if (isDefaultRole(roleToDelete)) return

            const currentRoles = formik.values[EAccountFormFields.SELECTED_ROLES]
            const updatedRoles = currentRoles.filter(role => role.title !== roleToDelete)

            formik.setFieldValue(`${EAccountFormFields.ACCOUNT_ROLES}.${roleToDelete}`, {
                [EAccountRoleFields.ACCOUNT]: null,
                [EAccountRoleFields.BILLING_ADDRESS]: null,
                [EAccountRoleFields.CONTACT]: null,
            })

            setRoleOptionsMap(prev => ({
                ...prev,
                [roleToDelete]: { billingAddresses: [], contacts: [] },
            }))

            formik.setFieldValue(EAccountFormFields.SELECTED_ROLES, updatedRoles)
        },
        [formik, isDefaultRole]
    )

    const prefillAccountsBySiteId = useCallback(
        async (siteId: number) => {
            try {
                const result = await getAccountBySiteId({ site_id: siteId }).unwrap()
                if (result?.accounts) {
                    const { accounts } = result
                    let currentRoles = [...formik.values[EAccountFormFields.SELECTED_ROLES]]
                    const newPrefilledRoles = new Set<EAccountRole>()

                    // The site->accounts endpoint only returns account ids, so fetch the
                    // account details to resolve the display name for the selected option.
                    const findAccountOption = async (accountId: number | undefined) => {
                        if (!accountId) return null
                        try {
                            const details = await fetchAccountDetails(accountId)
                            return details ? { id: accountId, title: details.account_name } : null
                        } catch {
                            return null
                        }
                    }

                    const ensureRoleInList = (roleType: EAccountRole) => {
                        if (currentRoles.some(r => r.title === roleType)) return
                        const roleInfo = accountRolesList?.find(r => r.type === roleType)
                        currentRoles = [
                            ...currentRoles,
                            { id: roleInfo?.id || roleType, title: roleType, disabled: isDefaultRole(roleType) },
                        ]
                    }

                    if (accounts.cpo?.account_id) {
                        const cpoAccount = await findAccountOption(accounts.cpo.account_id)
                        if (cpoAccount) {
                            ensureRoleInList(EAccountRole.CPO)
                            handleAccountChange(EAccountRole.CPO, cpoAccount)
                            newPrefilledRoles.add(EAccountRole.CPO)
                        }
                    }

                    if (accounts.host?.account_id) {
                        const hostAccount = await findAccountOption(accounts.host.account_id)
                        if (hostAccount) {
                            ensureRoleInList(EAccountRole.HOST)
                            handleAccountChange(EAccountRole.HOST, hostAccount)
                            newPrefilledRoles.add(EAccountRole.HOST)
                        }
                    }

                    if (accounts.franchise_id?.account_id) {
                        const franchiseAccount = await findAccountOption(accounts.franchise_id.account_id)
                        if (franchiseAccount) {
                            handleAccountChange(EAccountRole.BUYER, franchiseAccount)
                            newPrefilledRoles.add(EAccountRole.BUYER)
                        }
                        setIsFranchisePartner(true)
                        setIsFranchisePartnerLocked(true)
                    } else {
                        setIsFranchisePartnerLocked(false)
                    }

                    setPrefilledRoles(newPrefilledRoles)
                    formik.setFieldValue(EAccountFormFields.SELECTED_ROLES, currentRoles)
                }
            } catch (error) {
                console.error("Error fetching accounts by site:", error)
            }
        },
        [formik, getAccountBySiteId, fetchAccountDetails, accountRolesList, handleAccountChange, isDefaultRole]
    )

    const isDemolition = subModuleId === ESubModuleIds.DEMOLITION

    const handleDestinationSiteSelect = useCallback(
        async (site: SiteOption) => {
            formik.setFieldValue(EAccountFormFields.DESTINATION_SITE, site)
            setShowDestinationSites(false)

            if (!isDemolition) {
                if (site?.id) {
                    await prefillAccountsBySiteId(site.id)
                } else {
                    setPrefilledRoles(new Set())
                }
            }
        },
        [formik, isDemolition, prefillAccountsBySiteId]
    )

    // Prefill accounts when site is pre-set from outside (e.g., SiteTicketDetails).
    // For Demolition, prefill is based on source site; for all others, destination site.
    useEffect(() => {
        if (hasPrefilledFromSiteRef.current) return
        if (account_n_sites_form_values) return
        if (account_n_sites_form?.account_roles?.length) return

        const siteOption = isDemolition
            ? resolvePrefilledSite(account_n_sites_form.source_site, account_n_sites_form.source_site_id)
            : resolvePrefilledSite(account_n_sites_form.destination_site, account_n_sites_form.destination_site_id)

        if (!siteOption) return

        hasPrefilledFromSiteRef.current = true

        if (isDemolition) {
            formik.setFieldValue(EAccountFormFields.SOURCE_SITE, siteOption)
            prefillAccountsBySiteId(siteOption.id)
        } else {
            handleDestinationSiteSelect(siteOption)
        }
    }, [
        account_n_sites_form,
        account_n_sites_form_values,
        isDemolition,
        resolvePrefilledSite,
        prefillAccountsBySiteId,
        handleDestinationSiteSelect,
    ])

    const handleSourceSiteSelect = useCallback(
        async (site: SiteOption) => {
            formik.setFieldValue(EAccountFormFields.SOURCE_SITE, site)
            setShowSourceSites(false)

            if (isDemolition) {
                if (site?.id) {
                    await prefillAccountsBySiteId(site.id)
                } else {
                    setPrefilledRoles(new Set())
                }
            }
        },
        [formik, isDemolition, prefillAccountsBySiteId]
    )

    const handleFranchisePartnerToggle = useCallback(() => {
        const willBeFranchisePartner = !isFranchisePartner
        setIsFranchisePartner(willBeFranchisePartner)

        // SB-8432: on a CPO deal the buyer is Statiq, except on a franchise deal where it has to be
        // picked by hand. The checkbox is the user's own explicit statement about which of the two
        // this is, so it clears the buyer on the way in and puts the Statiq default back on the way out.
        if (!isCpoDeal || isFranchisePartnerLocked) return

        if (willBeFranchisePartner) {
            if (getSelectedAccount(EAccountRole.BUYER)) handleAccountChange(EAccountRole.BUYER, null)
            return
        }

        // Set straight from the lookup rather than clearing first — formik has not re-rendered yet,
        // so the "is it empty" check the default effect relies on would still read the old buyer.
        findStatiqAccount().then(statiqAccount => {
            if (statiqAccount) handleAccountChange(EAccountRole.BUYER, statiqAccount)
        })
    }, [
        isFranchisePartner,
        isCpoDeal,
        isFranchisePartnerLocked,
        getSelectedAccount,
        handleAccountChange,
        findStatiqAccount,
    ])

    const toggleDestinationSites = useCallback(() => {
        setShowDestinationSites(prev => !prev)
        setShowSourceSites(false)
    }, [])

    const toggleSourceSites = useCallback(() => {
        setShowSourceSites(prev => !prev)
        setShowDestinationSites(false)
    }, [])

    const isFormValid = useMemo(() => {
        const selectedRoles = formik.values[EAccountFormFields.SELECTED_ROLES]
        const hasValidRoles = selectedRoles.every(role => {
            const roleKey = role.title as EAccountRole
            const roleData = formik.values[EAccountFormFields.ACCOUNT_ROLES][roleKey]
            return (
                roleData?.[EAccountRoleFields.ACCOUNT] &&
                roleData?.[EAccountRoleFields.BILLING_ADDRESS] &&
                roleData?.[EAccountRoleFields.CONTACT]
            )
        })
        const hasDestinationSite = !!formik.values[EAccountFormFields.DESTINATION_SITE]
        const hasSourceSite = showSourceSite ? !!formik.values[EAccountFormFields.SOURCE_SITE] : true

        return hasValidRoles && hasDestinationSite && hasSourceSite
    }, [formik.values, showSourceSite])

    const destinationSiteError = useMemo(() => {
        return formik.submitCount > 0 && !formik.values[EAccountFormFields.DESTINATION_SITE]
    }, [formik.submitCount, formik.values])

    const sourceSiteError = useMemo(() => {
        return formik.submitCount > 0 && !formik.values[EAccountFormFields.SOURCE_SITE]
    }, [formik.submitCount, formik.values])

    const submitAccountNSitesForm = useCallback(() => {
        const selectedRoles = formik.values[EAccountFormFields.SELECTED_ROLES]
        const accountRolesData = formik.values[EAccountFormFields.ACCOUNT_ROLES]
        const destinationSite = formik.values[EAccountFormFields.DESTINATION_SITE]
        const sourceSite = formik.values[EAccountFormFields.SOURCE_SITE]

        const rolesWithValues: IAccountRoleData[] = selectedRoles
            .map(role => {
                const roleKey = role.title as EAccountRole
                const roleData = accountRolesData[roleKey]
                const accountTypeId = accountRolesList?.find(r => r.type === roleKey)?.id

                if (
                    roleData?.[EAccountRoleFields.ACCOUNT]?.id &&
                    roleData?.[EAccountRoleFields.BILLING_ADDRESS]?.id &&
                    roleData?.[EAccountRoleFields.CONTACT]?.id &&
                    accountTypeId
                ) {
                    return {
                        role: roleKey,
                        account_type_id: accountTypeId,
                        account_id: Number(roleData[EAccountRoleFields.ACCOUNT]!.id),
                        billing_address_id: Number(roleData[EAccountRoleFields.BILLING_ADDRESS]!.id),
                        contact_id: Number(roleData[EAccountRoleFields.CONTACT]!.id),
                        contact_type_id: roleData[EAccountRoleFields.CONTACT_TYPE_ID],
                    }
                }
                return null
            })
            .filter((role): role is IAccountRoleData => role !== null)

        dispatch(
            setAccountNSitesForm({
                destination_site_id: destinationSite?.id || null,
                source_site_id: sourceSite?.id || null,
                account_roles: rolesWithValues,
                is_franchise_partner: isFranchisePartner,
                franchise_mapping_date: isFranchisePartner ? new Date().toISOString().split("T")[0] : null,
            })
        )

        dispatch(
            setAccountNSitesFormValues({
                selectedRoles: selectedRoles,
                accountRoles: accountRolesData,
                destinationSite: destinationSite,
                sourceSite: sourceSite,
                is_franchise_partner: isFranchisePartner,
                franchise_mapping_date: isFranchisePartner ? new Date().toISOString().split("T")[0] : null,
            })
        )
    }, [formik.values, dispatch, accountRolesList, isFranchisePartner])

    const openAddAccountModal = useCallback((role: EAccountRole) => {
        setAccountFormRole(role)
        setEditAccountData(undefined)
        setIsEditMode(false)
        setShowAccountFormModal(true)
    }, [])

    const openEditAccountModal = useCallback(
        async (role: EAccountRole) => {
            const roleData = formik.values[EAccountFormFields.ACCOUNT_ROLES][role]
            const selectedAccount = roleData?.[EAccountRoleFields.ACCOUNT]

            if (!selectedAccount?.id) return

            setAccountFormRole(role)
            setIsEditMode(true)

            try {
                const accountData = await fetchAccountDetails(selectedAccount.id as number)
                if (accountData) {
                    setEditAccountData(accountData as unknown as AccountsTableData)
                    setShowAccountFormModal(true)
                }
            } catch (error) {
                console.error("Error fetching account details for edit:", error)
            }
        },
        [fetchAccountDetails, formik.values]
    )

    const closeAccountFormModal = useCallback(() => {
        setShowAccountFormModal(false)
        setAccountFormRole(null)
        setEditAccountData(undefined)
        setIsEditMode(false)
    }, [])

    const handleAccountFormSuccess = useCallback(async () => {
        if (!accountFormRole) return
        const roleData = formik.values[EAccountFormFields.ACCOUNT_ROLES][accountFormRole]
        const selectedAccount = roleData?.[EAccountRoleFields.ACCOUNT]

        if (selectedAccount?.id) {
            setLoadingMap(prev => ({ ...prev, [accountFormRole]: true }))

            try {
                const accountData = await fetchAccountDetails(selectedAccount.id as number)
                console.log(accountData, "accountData")
                if (accountData) {
                    setRoleOptionsMap(prev => ({
                        ...prev,
                        [accountFormRole]: mapAccountDetailsToOptions(accountData),
                    }))
                }
            } catch (error) {
                console.error("Error refetching account details after form submit:", error)
            } finally {
                setLoadingMap(prev => ({ ...prev, [accountFormRole]: false }))
            }
        }
    }, [accountFormRole, fetchAccountDetails, formik.values])

    return {
        states: {
            accountRolesOptions,
            accountsTrigger: getAccountsCompacted,
            roleOptionsMap,
            loadingMap,
            formik,
            isFormValid,
            selectedRoles: formik.values[EAccountFormFields.SELECTED_ROLES],
            showDestinationSites,
            showSourceSites,
            destinationSite: formik.values[EAccountFormFields.DESTINATION_SITE],
            sourceSite: formik.values[EAccountFormFields.SOURCE_SITE],
            destinationSiteError,
            sourceSiteError,
            showNewSiteForm,
            newSiteDetails,
            showNewSiteErrors,
            statesOptions,
            citiesOptions,
            isAddingSite,
            showAccountFormModal,
            accountFormRole,
            editAccountData,
            isEditMode,
            hasAddAccountPermission,
            hasEditAccountPermission,
            addSitesPermission,
            prefilledRoles,
            isFranchisePartner,
            isFranchisePartnerLocked,
        },
        functions: {
            handleRoleChange,
            handleAccountChange,
            handleBillingAddressChange,
            handleContactChange,
            handleDeleteRole,
            getFieldError,
            isDefaultRole,
            handleDestinationSiteSelect,
            handleSourceSiteSelect,
            toggleDestinationSites,
            toggleSourceSites,
            submitAccountNSitesForm,
            handleFranchisePartnerToggle,
            toggleNewSiteForm,
            handleNewSiteFieldChange,
            handleCoordinatePaste,
            handleAddNewSite,
            isValidLatitude,
            isValidLongitude,
            openAddAccountModal,
            openEditAccountModal,
            closeAccountFormModal,
            handleAccountFormSuccess,
        },
    }
}

export default useAccountNSites
